import { createSelector } from "@reduxjs/toolkit";
import {
  findQuestionsByCategory,
  findQuestionById
} from "../../../helpers/questionLookup";

export const selectAppraisal = (state) => state.appraisal;

export const getAppraisalData = createSelector(
  selectAppraisal,
  (appraisal) => appraisal.data
);

export const getIsLoading = createSelector(
  selectAppraisal,
  (appraisal) => !!appraisal.loading
);

export const getHasError = createSelector(
  selectAppraisal,
  (appraisal) => !!appraisal.error
);

export const getIsBlockedRegistration = createSelector(
  selectAppraisal,
  (appraisal) => appraisal.isBlockedRegistration
);

export const getVehicle = createSelector(
  getAppraisalData,
  (data) => data.vehicle
);

export const getQuestionsAndAnswers = createSelector(
  getAppraisalData,
  (data) => data.questionsAndAnswers
);

export const getUploadedImages = createSelector(
  getAppraisalData,
  (data) => data.images
);

// questions
export const getQuestions = createSelector(
  getAppraisalData,
  (data) => data.questions
);

export const getRedirectURI = createSelector(
  getAppraisalData,
  (data) => data.redirectURI
);

export const getRegistrationInput = createSelector(
  selectAppraisal,
  (appraisal) => appraisal.registrationInput
);

export const getFeatureToggles = createSelector(
  selectAppraisal,
  (appraisal) => appraisal.featureToggles
);

export const getQuestionsByCategory = (category) =>
  createSelector(
    getQuestions,
    (questions) => findQuestionsByCategory(questions, category)
  );

export const getFirstQuestionOfCategoryId = (category) =>
  createSelector(
    getQuestionsByCategory(category),
    (questions) => questions?.[0].id
  );

export const getQuestionById = (category, id) =>
  createSelector(getQuestionsByCategory(category), (questions) =>
    findQuestionById(questions, id)
  );

export const getNextQuestionId = (id, category) =>
  createSelector(getQuestionsByCategory(category), (questions) => {
    const questionsIds = questions.map((question) => question.id);
    const index = questionsIds && questionsIds.indexOf(parseInt(id));
    return questionsIds ? questionsIds[index + 1] : null;
  });

export const getAssumptionAnswers = createSelector(
  getQuestionsAndAnswers,
  getQuestionsByCategory("Factual"),
  getQuestionsByCategory("Condition"),
  (questionsAndAnswers, factualQuestions, conditionQuestions) => {
    const qAas = Object.assign({}, questionsAndAnswers);
    const factualQuestionIds = factualQuestions.map((q) => q.id);
    const conditionQuestionIds = conditionQuestions.map((q) => q.id);

    const idsToRemove = factualQuestionIds.concat(conditionQuestionIds);

    idsToRemove && idsToRemove.forEach((id) => delete qAas[id]);

    return qAas;
  }
);

export const getFactualAndConditionQuestionGroups = createSelector(
  getQuestions,
  (questions) => questions.filter((q) => q.category !== "Assumption")
);

// source system
export const getSourceSystem = createSelector(
  getAppraisalData,
  (data) => data.sourceSystem
);

export const getSourceSystemName = createSelector(
  getAppraisalData,
  (data) => data.sourceSystemName
);

export const getSourceSystemIsSell = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "sell"
);

export const getSourceSystemIsValuationAdmin = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "valuation-admin"
);

export const getSourceSystemIsBranchAppraisal = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "branch-appraisal"
);

export const getSourceSystemIsCNP = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "sales-cnp"
);

export const getSourceSystemIsSalesOffice = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "sales-office"
);

export const getSourceSystemNameIsSell = createSelector(
  getSourceSystemName,
  (sourceSystemName) => sourceSystemName === "sell"
);

export const getSourceSystemIsQuotations = createSelector(
  getSourceSystem,
  (sourceSystem) => sourceSystem === "quotations"
);

export const getSourceSystemContent = createSelector(
  getSourceSystem,
  getRedirectURI,
  (sourceSystem, redirectURI) => {
    const valueMyCar = {
      photosRequired: true,
      errorUri: "/",
      headerTitle: "Value my car",
      stepCount: 16,
      initialOfferContent: {
        cta: "Save and submit valuation",
        ctaMicro: "You've made it! We'll take it from here.",
        assumptionsCopy:
          "Your personalised offer is subject to a branch valuation and is based on these assumptions.",
        renderImageUpload: true,
        stepNumber: 15
      },
      quickValuation: false,
      showPrivacyStatement: false,
      title: "value my car"
    };

    const branchJourney = {
      photosRequired: true,
      errorUri: "/valuation-admin",
      headerTitle: "Branch valuation",
      stepCount: 9,
      initialOfferContent: {
        cta: "Continue",
        ctaMicro:
          "Next, the branch manager will give this a once over and lock in this valuation.",
        assumptionsCopy:
          "Your personalised offer is subject to a full appraisal and is based on these assumptions.",
        renderImageUpload: false,
        stepNumber: 8
      },
      quickValuation: false,
      showPrivacyStatement: true,
      compactJourney: true
    };

    const branchAppraisal = {
      photosRequired: true,
      errorUri: "/valuation-admin",
      headerTitle: "Value my car",
      stepCount: 8,
      initialOfferContent: {
        cta: "Save and submit valuation",
        ctaMicro:
          "Next, the branch manager will give this a once over and lock in this valuation.",
        assumptionsCopy:
          "Your personalised offer is subject to a full appraisal and is based on these assumptions.",
        renderImageUpload: false,
        stepNumber: 7
      },
      quickValuation: false,
      showPrivacyStatement: true,
      compactJourney: true
    };

    const sellMyCar = {
      photosRequired: false,
      errorUri: "/sell-my-car",
      headerTitle: "Sell my car",
      stepCount: 6,
      initialOfferContent: {
        cta: "Continue without full valuation",
        ctaMicro:
          "Alternatively, you can email yourself this valuation or book an appointment.",
        assumptionsCopy:
          "*Your offer is subject to a full appraisal and based on",
        renderImageUpload: false,
        stepNumber: 3,
        ctaPersonalised: "Continue"
      },
      quickValuation: true,
      showPrivacyStatement: false
    };

    const salesCNP = {
      photosRequired: false,
      errorUri: "/valuation-admin",
      headerTitle: "Value my car",
      stepCount: 6,
      initialOfferContent: {
        cta: "Save and submit valuation",
        ctaMicro: "You've made it! We'll take it from here.",
        assumptionsCopy:
          "Your personalised offer is subject to a branch valuation and is based on these assumptions.",
        renderImageUpload: false,
        stepNumber: 5,
        ctaPersonalised: "Continue"
      },
      quickValuation: false,
      showPrivacyStatement: false
    };

    const quotations = {
      photosRequired: false,
      errorUri: redirectURI,
      headerTitle: "Build your deal",
      stepCount: 12,
      initialOfferContent: {
        cta: "Continue",
        ctaMicro:
          "Next tell us if you have any outstanding finance on this car",
        assumptionsCopy:
          "*Your offer is subject to a full physical appraisal and based on ",
        renderImageUpload: false,
        stepNumber: 11,
        ctaPersonalised: "Continue"
      },
      quickValuation: true,
      showPrivacyStatement: false
    };

    switch (sourceSystem) {
      case "valuation-admin":
        return branchJourney;
      case "value-my-car":
        return valueMyCar;
      case "sales-office":
        return branchJourney;
      case "branch-appraisal":
        return branchAppraisal;
      case "sales-office-customer":
        return valueMyCar;
      case "sell":
        return sellMyCar;
      case "sales-cnp":
        return salesCNP;
      case "quotations":
        return quotations;
      case null:
        return valueMyCar;
      default:
        return valueMyCar;
    }
  }
);

export const getSourceSystemIsCompactView = createSelector(
  getSourceSystemContent,
  (sourceSystemContent) => {
    return sourceSystemContent.compactJourney === true;
  }
);

// pages have required data
export const getMileageIsReadyToRender = createSelector(
  getVehicle,
  getQuestions,
  (vehicle, questions) => !!vehicle && !!questions
);

// uploaded images

export const getFrontImage = createSelector(getUploadedImages, (images) =>
  !!images.length
    ? images.find((img) =>
        img.imageTags.some((tag) => tag.tagDescription === "front")
      ).imageIdentifier
    : null
);
